<template>
  <div class="inspection-task-container">
    <el-row :gutter="16">
      <!-- 左侧：巡检计划切换（Token 化卡片导航） -->
      <el-col :span="4">
        <div class="ds-side-card">
          <div class="ds-side-card__title">{{ $t('inspectionTask.inspectionPlan') }}</div>
          <div class="ds-plan-list">
            <div v-for="item in plans" :key="item.inspectionPlanId" class="ds-plan-item"
              :class="{ 'ds-plan-item--active': conditions.inspectionPlanId === item.inspectionPlanId }"
              @click="switchPlan(item)">
              <i class="el-icon-document"></i>
              <span class="ds-plan-item__text">{{ item.inspectionPlanName }}</span>
            </div>
          </div>
        </div>
      </el-col>

      <el-col :span="20">
        <!-- (1) PageHeader -->
        <ds-page-header :title="$t('inspectionTask.page.title')" :subtitle="$t('inspectionTask.page.subtitle')" />

        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.planUserName" clearable
                :placeholder="$t('inspectionTask.executor')" @keyup.enter.native="queryInspectionTasks" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.startTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('inspectionTask.actualStartTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.endTime" type="datetime" class="w-full"
                value-format="yyyy-MM-dd HH:mm:ss" :placeholder="$t('inspectionTask.actualEndTime')" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.state" clearable class="w-full"
                :placeholder="$t('inspectionTask.inspectionStatus')">
                <el-option v-for="item in stateTypes" :key="item.statusCd" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="queryInspectionTasks">
              {{ $t('inspectionTask.query') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetConditions">
              {{ $t('inspectionTask.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="loadInspectionTasks">
            <template #left>
              <span class="ds-panel__title">{{ $t('inspectionTask.inspectionTask') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="inspectionTasks" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('inspectionTask.page.empty')" @retry="loadInspectionTasks">
            <el-table-column prop="taskId" :label="$t('inspectionTask.taskCode')" align="center" width="150">
              <template slot-scope="{row}">
                <span class="ds-num">{{ row.taskId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="inspectionPlanName" :label="$t('inspectionTask.inspectionPlan')" align="left"
              min-width="150" show-overflow-tooltip />
            <!-- 计划巡检时间：起止两行 -->
            <el-table-column :label="$t('inspectionTask.inspectorTime')" align="center" width="160">
              <template slot-scope="{row}">
                <div class="ds-num">{{ row.planInsTime || '-' }}</div>
                <div class="ds-num ds-cell-sub">{{ row.planEndTime || '-' }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="actInsTime" :label="$t('inspectionTask.actualTime')" align="center" width="160">
              <template slot-scope="{row}">
                <span class="ds-num">{{ row.actInsTime || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="originalPlanUserName" :label="$t('inspectionTask.plannedInspector')" align="center"
              width="110">
              <template slot-scope="{row}">
                {{ row.originalPlanUserName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="planUserName" :label="$t('inspectionTask.currentInspector')" align="center"
              width="100" />
            <el-table-column prop="transferDesc" :label="$t('inspectionTask.transferDesc')" align="left" min-width="130"
              show-overflow-tooltip>
              <template slot-scope="{row}">
                {{ row.transferDesc || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="signTypeName" :label="$t('inspectionTask.inspectionMethod')" align="center"
              width="100" />
            <!-- 状态 Tag：20200408（已超时）/ 20200409（缺勤）用危险色 -->
            <el-table-column :label="$t('inspectionTask.inspectionStatus')" align="center" width="120">
              <template slot-scope="{row}">
                <ds-status-tag :type="resolveState(row)" :label="row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column :label="$t('inspectionTask.operation')" align="center" width="200" fixed="right">
              <template slot-scope="{row}">
                <el-button v-if="row.state === '20200406' || row.state === '20200405'" size="mini" type="text"
                  @click="openTransfer(row)">
                  {{ $t('inspectionTask.flow') }}
                </el-button>
                <el-button size="mini" type="text" @click="openDetail(row)">
                  {{ $t('inspectionTask.detail') }}
                </el-button>
                <el-button v-if="hasPrivilege('502022031612550001')" size="mini" type="text" class="ds-btn-danger"
                  @click="openDelete(row)">
                  {{ $t('inspectionTask.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="total" :page="currentPage" :size="pageSize"
            layout="total, prev, pager, next, jumper" @current-change="handlePageChange">
            <template #summary>
              <span class="ds-tip-text">{{ $t('inspectionTask.note') }}</span>
            </template>
          </ds-pagination-bar>
        </div>
      </el-col>
    </el-row>

    <InspectionTaskDetail ref="inspectionTaskDetail" />
    <InspectionTaskTransfer ref="inspectionTaskTransfer" @success="loadInspectionTasks" />
    <DeleteInspectionTask ref="deleteInspectionTask" @success="loadInspectionTasks" />
  </div>
</template>

<script>
import InspectionTaskDetail from '@/components/inspection/InspectionTaskDetail'
import InspectionTaskTransfer from '@/components/inspection/InspectionTaskTransfer'
import DeleteInspectionTask from '@/components/inspection/DeleteInspectionTask'
import {
  listInspectionPlans,
  listInspectionTasks,
} from '@/api/inspection/inspectionTaskApi'
import { getDict } from '@/api/community/communityApi'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'InspectionTaskList',
  components: {
    InspectionTaskDetail,
    InspectionTaskTransfer,
    DeleteInspectionTask,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsStatusTag,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      plans: [],
      inspectionTasks: [],
      stateTypes: [],
      conditions: {
        planUserName: '',
        inspectionPlanId: '',
        startTime: '',
        endTime: '',
        state: '',
        orderByDesc: 'desc'
      },
      currentPage: 1,
      pageSize: 10,
      total: 0
    }
  },
  async created() {
    await this.loadPlans()
    await this.loadStateTypes()
    this.loadInspectionTasks()
  },
  methods: {
    async loadPlans() {
      try {
        const { data } = await listInspectionPlans()
        this.plans = [{ inspectionPlanName: this.$t('common.all'), inspectionPlanId: '' }, ...(data || [])]
      } catch (error) {
        console.error('加载巡检计划失败:', error)
      }
    },
    async loadStateTypes() {
      try {
        const res = await getDict('inspection_task', 'state')
        this.stateTypes = Array.isArray(res) ? res : []
      } catch (error) {
        console.error('加载状态类型失败:', error)
      }
    },
    async loadInspectionTasks() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          ...this.conditions,
          page: this.currentPage,
          row: this.pageSize
        }
        const res = await listInspectionTasks(params)

        // 兼容后端在「无匹配数据」时返回 { code:0, records:0 } 而不带 data 字段的情况。
        this.inspectionTasks = Array.isArray(res.inspectionTasks) ? res.inspectionTasks : []
        this.total = res.total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('inspectionTask.page.loadFailed')
        console.error('加载巡检任务失败:', error)
      } finally {
        this.loading = false
      }
    },
    switchPlan(plan) {
      this.conditions.inspectionPlanId = plan.inspectionPlanId
      this.currentPage = 1
      this.loadInspectionTasks()
    },
    queryInspectionTasks() {
      this.currentPage = 1
      this.loadInspectionTasks()
    },
    resetConditions() {
      this.conditions = {
        planUserName: '',
        inspectionPlanId: '',
        startTime: '',
        endTime: '',
        state: '',
        orderByDesc: 'desc'
      }
      this.currentPage = 1
      this.loadInspectionTasks()
    },
    handlePageChange(page) {
      this.currentPage = page
      this.loadInspectionTasks()
    },
    openDetail(task) {
      this.$refs.inspectionTaskDetail.open(task)
    },
    openTransfer(task) {
      this.$refs.inspectionTaskTransfer.open(task)
    },
    openDelete(task) {
      this.$refs.deleteInspectionTask.open(task)
    },
    /**
     * 状态语义类型：20200408「已超时」/ 20200409「缺勤」为危险色，其余按文案语义推断。
     */
    resolveState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'inspectionTask',
        text: row.stateName || ''
      }).type
    }
  }
}
</script>

<style lang="scss" scoped>
.inspection-task-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧导航卡片 */
.ds-side-card {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    padding: var(--ds-spacing-sm) var(--ds-spacing-md);
    font-size: var(--ds-font-size-sm);
    font-weight: 600;
    color: var(--ds-gray-700);
    background: var(--ds-gray-100);
    border-bottom: 1px solid var(--ds-gray-300);
  }
}

/* 计划列表项 */
.ds-plan-list {
  padding: var(--ds-spacing-xs);
  max-height: calc(100vh - 260px);
  overflow-y: auto;
}

.ds-plan-item {
  display: flex;
  align-items: center;
  gap: var(--ds-spacing-xs);
  padding: var(--ds-spacing-sm) var(--ds-spacing-md);
  margin-bottom: 2px;
  border-radius: var(--ds-radius-sm);
  cursor: pointer;
  transition: background-color var(--ds-transition-fast), color var(--ds-transition-fast);
  color: var(--ds-gray-700);
  font-size: var(--ds-font-size-sm);

  &:hover {
    background-color: var(--ds-gray-100);
  }

  &--active {
    background-color: var(--ds-primary-sub);
    color: var(--ds-primary);
    font-weight: 600;
  }

  &__text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

/* 表格次要文字 */
.ds-cell-sub {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
}

/* 分页条左侧提示文案 */
.ds-tip-text {
  font-size: var(--ds-font-size-xs);
  color: var(--ds-gray-500);
  line-height: 1.6;
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
